import {
  isFixedRateLoanProductTemplate,
  isFloatingRateLoanProductTemplate,
  isOTCForwardProductTemplate,
  isOTCOptionProductTemplate,
  isOTCRepoProductTemplate,
} from '@src/types/OTC/otcSecurityTypeGuards';
import { useMemo } from 'react';
import { map } from 'rxjs/operators';
import { useObservableValue } from '../../hooks/useObservable';
import { wsScanToMap } from '../../pipes';
import type { Security } from '../../types/Security';
import { shouldDeleteSecurityFromIndex, type SecurityMemoryObservable } from './utils';

export function useOTCIndexes(securityMemoryObs: SecurityMemoryObservable) {
  const otcFixedRateLoanProductTemplatesByDisplaySymbolValue = useOTCDisplaySymbolMap(
    securityMemoryObs,
    isFixedRateLoanProductTemplate,
    new Map()
  );
  const otcForwardProductTemplatesByDisplaySymbolValue = useOTCDisplaySymbolMap(
    securityMemoryObs,
    isOTCForwardProductTemplate,
    new Map()
  );
  const otcOptionProductTemplatesByDisplaySymbolValue = useOTCDisplaySymbolMap(
    securityMemoryObs,
    isOTCOptionProductTemplate,
    new Map()
  );
  const otcFloatingRateLoanProductTemplatesByDisplaySymbolValue = useOTCDisplaySymbolMap(
    securityMemoryObs,
    isFloatingRateLoanProductTemplate,
    new Map()
  );
  const otcRepoProductTemplatesByDisplaySymbolValue = useOTCDisplaySymbolMap(
    securityMemoryObs,
    isOTCRepoProductTemplate,
    new Map()
  );

  return useMemo(
    () => ({
      otcFixedRateLoanProductTemplatesByDisplaySymbol: otcFixedRateLoanProductTemplatesByDisplaySymbolValue,
      otcForwardProductTemplatesByDisplaySymbol: otcForwardProductTemplatesByDisplaySymbolValue,
      otcOptionProductTemplatesByDisplaySymbol: otcOptionProductTemplatesByDisplaySymbolValue,
      otcFloatingRateLoanProductTemplatesByDisplaySymbol: otcFloatingRateLoanProductTemplatesByDisplaySymbolValue,
      otcRepoProductTemplatesByDisplaySymbol: otcRepoProductTemplatesByDisplaySymbolValue,
    }),
    [
      otcFixedRateLoanProductTemplatesByDisplaySymbolValue,
      otcFloatingRateLoanProductTemplatesByDisplaySymbolValue,
      otcForwardProductTemplatesByDisplaySymbolValue,
      otcOptionProductTemplatesByDisplaySymbolValue,
      otcRepoProductTemplatesByDisplaySymbolValue,
    ]
  );
}

function useOTCDisplaySymbolMap<TOTC extends Security>(
  securityMemoryObs: SecurityMemoryObservable,
  isOTCType: (security: Security) => security is TOTC,
  initialValue: Map<string, TOTC>
) {
  const otcMapObs = useMemo(
    () =>
      securityMemoryObs.pipe(
        map(({ latestMessage }) => latestMessage),
        wsScanToMap<Security, string, TOTC, TOTC>({
          getUniqueKey: security => security.DisplaySymbol,
          getInsertable: security => security,
          newMapEachUpdate: true,
          isRelevant: isOTCType,
          shouldDelete: shouldDeleteSecurityFromIndex,
        })
      ),
    [isOTCType, securityMemoryObs]
  );

  return useObservableValue(() => otcMapObs, [otcMapObs], initialValue);
}
